import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue } from "framer-motion";
import {
  X,
  Send,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Trash2,
  Sunrise,
  Loader2,
  Check,
  Settings as SettingsIcon,
  Moon,
  AlertTriangle,
} from "lucide-react";
import { EggSprite, WakeBall } from "./AiSprite";
import { aiChat, aiErrorText, fmtUsd } from "../ai/aiApi";
import {
  buildAiContext,
  buildSystemPrompt,
  parseActions,
  describeAction,
  BRIEFING_PROMPT,
  SCOPE_LABELS,
} from "../ai/context";
import { speak, stopSpeaking, startListening, ttsSupported, sttSupported } from "../ai/speech";

const SPRITE_W = 72;
const SPRITE_H = 90;
const POS_KEY = "xz-ai-pos";
const HISTORY_LIMIT = 50;
const historyKey = (userName) => `xz-ai-chat:${userName || "anon"}`;

const isMobile = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

const readPos = () => {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
  } catch (e) {
    /* ignore */
  }
  return null;
};
const clampPos = (p) => ({
  x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - SPRITE_W - 8)),
  y: Math.min(Math.max(72, p.y), Math.max(72, window.innerHeight - SPRITE_H - 8)),
});
const randomPos = () =>
  clampPos({
    x: 8 + Math.random() * Math.max(0, window.innerWidth - SPRITE_W - 16),
    y: 72 + Math.random() * Math.max(0, window.innerHeight - SPRITE_H - 80),
  });

// Minimal Markdown: paragraphs, bullets, **bold**, `code`.
const inline = (s) => {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("**")) parts.push(<strong key={m.index}>{t.slice(2, -2)}</strong>);
    else parts.push(
      <code key={m.index} className="px-1 rounded bg-gray-900/60 text-cyan-200 text-[12px]">
        {t.slice(1, -1)}
      </code>,
    );
    last = m.index + t.length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
};
const Markdown = ({ text }) => {
  const blocks = [];
  let list = null;
  String(text || "")
    .split("\n")
    .forEach((line, i) => {
      const t = line.trim();
      const li = /^[-*•]\s+(.*)/.exec(t) || /^\d+[.)]\s+(.*)/.exec(t);
      if (li) {
        if (!list) {
          list = [];
          blocks.push({ type: "ul", items: list, key: i });
        }
        list.push(li[1]);
      } else {
        list = null;
        if (t) blocks.push({ type: "p", text: t.replace(/^#+\s*/, ""), key: i, heading: /^#+\s/.test(t) });
      }
    });
  return (
    <div className="space-y-1.5">
      {blocks.map((b) =>
        b.type === "ul" ? (
          <ul key={b.key} className="list-disc pl-4 space-y-0.5">
            {b.items.map((it, j) => (
              <li key={j}>{inline(it)}</li>
            ))}
          </ul>
        ) : (
          <p key={b.key} className={b.heading ? "font-semibold text-cyan-200" : ""}>
            {inline(b.text)}
          </p>
        ),
      )}
    </div>
  );
};

/**
 * The AI companion: a wandering, draggable egg sprite (desktop) and a chat
 * panel (desktop popover / mobile sheet). It never speaks or acts on its own:
 * it answers when asked, and every proposed change waits for Confirm.
 */
const AiCompanion = ({
  name,
  awake,
  onSleep,
  configured,
  settings,
  getState,
  focus,
  onClearFocus,
  onApplyAction,
  onOpenSettings,
  userName,
  openSignal,
}) => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // hatch | idle
  const [mood, setMood] = useState("idle");
  const [facing, setFacing] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [warn, setWarn] = useState(null);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(!!(settings && settings.tts));
  const [mobile, setMobile] = useState(isMobile());
  const stopListenRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const wanderRef = useRef({ timer: 0, anims: [] });

  useEffect(() => setTtsOn(!!(settings && settings.tts)), [settings]);

  // Chat history lives only in this browser.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(historyKey(userName)) || "[]");
      setMessages(Array.isArray(saved) ? saved.slice(-HISTORY_LIMIT) : []);
    } catch (e) {
      setMessages([]);
    }
  }, [userName]);
  useEffect(() => {
    try {
      localStorage.setItem(historyKey(userName), JSON.stringify(messages.slice(-HISTORY_LIMIT)));
    } catch (e) {
      /* ignore */
    }
  }, [messages, userName]);

  // Viewport changes: keep the sprite on screen; switch desktop/mobile layouts.
  useEffect(() => {
    const onResize = () => {
      setMobile(isMobile());
      const p = clampPos({ x: x.get(), y: y.get() });
      x.set(p.x);
      y.set(p.y);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [x, y]);

  // Initial position + hatch animation on wake.
  useEffect(() => {
    if (!awake) {
      setOpen(false);
      stopSpeaking();
      return;
    }
    const p = clampPos(readPos() || { x: window.innerWidth - SPRITE_W - 24, y: window.innerHeight - SPRITE_H - 96 });
    x.set(p.x);
    y.set(p.y);
    setPhase("hatch");
    const t = setTimeout(() => setPhase("idle"), 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awake]);

  // External "open the chat" requests (per-panel Ask AI buttons).
  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  // Wander: every 9–16 s, when idle and visible, stroll to a random spot.
  useEffect(() => {
    if (!awake || mobile || open || dragging || phase !== "idle" || busy) return undefined;
    let cancelled = false;
    const w = wanderRef.current;
    const schedule = () => {
      w.timer = setTimeout(() => {
        if (cancelled || document.visibilityState !== "visible") {
          schedule();
          return;
        }
        const target = randomPos();
        setFacing(target.x < x.get() ? -1 : 1);
        const dur = 1.4 + Math.random() * 1.4;
        w.anims = [
          animate(x, target.x, { duration: dur, ease: "easeInOut" }),
          animate(y, target.y, { duration: dur, ease: "easeInOut" }),
        ];
        setTimeout(() => {
          if (!cancelled) {
            try {
              localStorage.setItem(POS_KEY, JSON.stringify(target));
            } catch (e) {
              /* ignore */
            }
          }
        }, dur * 1000 + 50);
        schedule();
      }, 9000 + Math.random() * 7000);
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(w.timer);
      w.anims.forEach((a) => a && a.stop && a.stop());
      w.anims = [];
    };
  }, [awake, mobile, open, dragging, phase, busy, x, y]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, busy]);

  useEffect(() => {
    if (open && inputRef.current && !mobile) inputRef.current.focus();
  }, [open, mobile]);

  const scopes = (settings && settings.scopes) || null;
  const focusLabel = focus ? SCOPE_LABELS[focus] : null;

  const send = useCallback(
    async (text, kind = "chat") => {
      const content = String(text || "").trim();
      if (!content || busy) return;
      if (!configured) {
        setError("Set up a provider, key and model in Settings → AI companion first.");
        return;
      }
      setError(null);
      setWarn(null);
      setInput("");
      const next = [...messages, { role: "user", content, at: Date.now() }];
      setMessages(next);
      setBusy(true);
      setMood("busy");
      try {
        const state = getState ? getState() : {};
        const context = buildAiContext(state, scopes, focus);
        const system = buildSystemPrompt({
          name,
          context,
          focusLabel,
          today: state.today || new Date().toISOString().slice(0, 10),
          userName,
        });
        const history = next.slice(-12).map((m) => ({ role: m.role, content: m.content }));
        const r = await aiChat({ system, messages: history, kind });
        const { clean, actions } = parseActions(r.text || "");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: clean || "(empty reply)",
            actions: actions.map((a, i) => ({ ...a, _id: `${Date.now()}-${i}`, _state: "pending" })),
            usage: {
              inTokens: r.inTokens,
              outTokens: r.outTokens,
              costUsd: r.costUsd,
              priceKnown: r.priceKnown,
              model: r.model,
              latencyMs: r.latencyMs,
              truncated: r.truncated,
            },
            at: Date.now(),
          },
        ]);
        if (r.warn80) {
          setWarn(
            `Heads-up: ≈ ${fmtUsd(r.monthCost)} of your ${fmtUsd(r.capUsd)} monthly AI budget is used.`,
          );
        }
        setMood("happy");
        setTimeout(() => setMood("idle"), 2500);
        if (ttsOn) speak(clean, settings && settings.voice);
      } catch (e) {
        setError(aiErrorText(e, "The AI call failed."));
        setMood("idle");
      } finally {
        setBusy(false);
      }
    },
    [busy, configured, messages, getState, scopes, focus, name, focusLabel, userName, ttsOn, settings],
  );

  const applyAction = (msgIdx, action) => {
    let result = { ok: false, message: "Not applied." };
    try {
      result = (onApplyAction && onApplyAction(action)) || result;
    } catch (e) {
      result = { ok: false, message: (e && e.message) || "Failed." };
    }
    setMessages((prev) =>
      prev.map((m, i) =>
        i !== msgIdx
          ? m
          : {
              ...m,
              actions: m.actions.map((a) =>
                a._id === action._id
                  ? { ...a, _state: result.ok ? "done" : "failed", _result: result.message }
                  : a,
              ),
            },
      ),
    );
  };
  const dismissAction = (msgIdx, action) => {
    setMessages((prev) =>
      prev.map((m, i) =>
        i !== msgIdx
          ? m
          : { ...m, actions: m.actions.map((a) => (a._id === action._id ? { ...a, _state: "dismissed" } : a)) },
      ),
    );
  };

  const toggleListening = () => {
    if (listening) {
      if (stopListenRef.current) stopListenRef.current();
      stopListenRef.current = null;
      setListening(false);
      return;
    }
    const base = input;
    const stop = startListening({
      onText: (t, final) => setInput(final ? `${base}${base ? " " : ""}${t}` : `${base}${base ? " " : ""}${t}`),
      onEnd: () => {
        setListening(false);
        stopListenRef.current = null;
      },
      onError: () => {
        setListening(false);
        stopListenRef.current = null;
      },
    });
    if (stop) {
      stopListenRef.current = stop;
      setListening(true);
    }
  };

  const clearHistory = () => {
    setMessages([]);
    setError(null);
    setWarn(null);
  };

  const onDragEnd = () => {
    setDragging(false);
    const p = clampPos({ x: x.get(), y: y.get() });
    x.set(p.x);
    y.set(p.y);
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(p));
    } catch (e) {
      /* ignore */
    }
  };

  const dragConstraints = useMemo(
    () => ({
      left: 8,
      top: 72,
      right: Math.max(8, window.innerWidth - SPRITE_W - 8),
      bottom: Math.max(72, window.innerHeight - SPRITE_H - 8),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mobile, awake],
  );

  if (!awake) return null;

  const panel = (
    <motion.div
      key="panel"
      role="dialog"
      aria-label={`${name} chat`}
      initial={mobile ? { y: 40, opacity: 0 } : { scale: 0.96, opacity: 0, y: 8 }}
      animate={mobile ? { y: 0, opacity: 1 } : { scale: 1, opacity: 1, y: 0 }}
      exit={mobile ? { y: 40, opacity: 0 } : { scale: 0.96, opacity: 0, y: 8 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      // Sits above the floating sync indicator (bottom-right, ~3.5rem tall) so
      // the two never overlap.
      className={`fixed z-[46] flex flex-col bg-gray-800/95 backdrop-blur border-2 border-purple-500/40 shadow-2xl ${
        mobile
          ? "inset-x-2 bottom-[4.5rem] rounded-3xl max-h-[72vh]"
          : "right-4 bottom-[4.75rem] w-[380px] max-w-[calc(100vw-2rem)] rounded-3xl max-h-[calc(100vh-7rem)]"
      }`}
    >
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700">
        <EggSprite size={26} mood={busy ? "busy" : mood} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-purple-200 truncate">{name}</div>
          <div className="text-[11px] text-gray-400 truncate">
            {configured ? settings && settings.model ? settings.model : "ready" : "not set up yet"}
            {focusLabel && (
              <button
                type="button"
                onClick={onClearFocus}
                className="ml-1.5 px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-400/40"
                title="Click to clear the panel focus"
              >
                {focusLabel} ×
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => send(BRIEFING_PROMPT, "briefing")}
          disabled={busy || !configured}
          className="tap p-1.5 rounded-lg text-yellow-300 hover:bg-gray-700/60 disabled:opacity-40"
          title="Briefing for today"
          aria-label="Briefing for today"
        >
          <Sunrise className="w-4 h-4" />
        </button>
        {ttsSupported() && (
          <button
            type="button"
            onClick={() => {
              if (ttsOn) stopSpeaking();
              setTtsOn((v) => !v);
            }}
            className={`tap p-1.5 rounded-lg hover:bg-gray-700/60 ${ttsOn ? "text-cyan-300" : "text-gray-500"}`}
            title={ttsOn ? "Stop reading replies aloud" : "Read replies aloud"}
            aria-pressed={ttsOn}
          >
            {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={clearHistory}
          disabled={!messages.length}
          className="tap p-1.5 rounded-lg text-gray-400 hover:bg-gray-700/60 hover:text-red-300 disabled:opacity-40"
          title="Clear this conversation"
          aria-label="Clear conversation"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onOpenSettings}
          className="tap p-1.5 rounded-lg text-gray-400 hover:bg-gray-700/60 hover:text-cyan-300"
          title="AI settings"
          aria-label="AI settings"
        >
          <SettingsIcon className="w-4 h-4" />
        </button>
        {mobile && (
          <button
            type="button"
            onClick={onSleep}
            className="tap p-1.5 rounded-lg text-gray-400 hover:bg-gray-700/60"
            title={`Put ${name} to sleep`}
            aria-label={`Put ${name} to sleep`}
          >
            <Moon className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="tap p-1.5 rounded-lg text-gray-400 hover:bg-gray-700/60"
          aria-label="Close chat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm text-gray-200 min-h-[160px]">
        {!configured && (
          <div className="rounded-2xl bg-gray-700/50 border border-gray-600 p-3 text-xs text-gray-300">
            Hi! I’m {name}. Add an API key and pick a model in Settings → AI companion and I can answer
            questions about your challenge, goals, notes and more.
            <button
              type="button"
              onClick={onOpenSettings}
              className="block mt-2 px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-200 border border-purple-400/40 font-semibold"
            >
              Open AI settings
            </button>
          </div>
        )}
        {configured && messages.length === 0 && (
          <div className="rounded-2xl bg-gray-700/50 border border-gray-600 p-3 text-xs text-gray-300">
            Ask me anything about your data — “what’s due this week?”, “summarise my notes”, “add a goal to
            read two papers by Friday”. Changes are only proposed; you confirm them.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.at || i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={`max-w-[92%] rounded-2xl px-3 py-2 ${
                m.role === "user"
                  ? "bg-cyan-500/20 border border-cyan-500/30 text-cyan-100"
                  : "bg-gray-700/60 border border-gray-600"
              }`}
            >
              {m.role === "user" ? <p className="whitespace-pre-wrap">{m.content}</p> : <Markdown text={m.content} />}
              {m.actions && m.actions.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {m.actions.map((a) => (
                    <div key={a._id} className="rounded-xl bg-gray-800/80 border border-purple-500/30 px-2.5 py-2 text-xs">
                      <div className="text-purple-100">{describeAction(a)}</div>
                      {a._state === "pending" ? (
                        <div className="flex gap-1.5 mt-1.5">
                          <button
                            type="button"
                            onClick={() => applyAction(i, a)}
                            className="px-2.5 py-1 rounded-lg bg-purple-500/30 text-purple-100 border border-purple-400/50 font-semibold flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissAction(i, a)}
                            className="px-2.5 py-1 rounded-lg bg-gray-700 text-gray-300 border border-gray-600"
                          >
                            Dismiss
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`mt-1 ${
                            a._state === "done" ? "text-green-300" : a._state === "failed" ? "text-red-300" : "text-gray-500"
                          }`}
                        >
                          {a._state === "done" ? `✓ ${a._result || "Done"}` : a._state === "failed" ? `✗ ${a._result || "Failed"}` : "Dismissed"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {m.usage && (
                <div className="mt-1.5 text-[10px] text-gray-500">
                  {m.usage.inTokens}↑ {m.usage.outTokens}↓ tokens ·{" "}
                  {m.usage.priceKnown ? `≈ ${fmtUsd(m.usage.costUsd)}` : "cost unknown"}
                  {m.usage.latencyMs ? ` · ${(m.usage.latencyMs / 1000).toFixed(1)}s` : ""}
                  {m.usage.truncated ? " · reply was cut short by the model" : ""}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {name} is thinking…
          </div>
        )}
        {warn && (
          <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs p-2 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            {warn}
          </div>
        )}
        {error && (
          <div role="alert" className="rounded-xl bg-red-600/10 border border-red-500/30 text-red-300 text-xs p-2">
            {error}
          </div>
        )}
      </div>

      {/* composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="flex items-end gap-2 p-2 border-t border-gray-700"
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          rows={1}
          placeholder={configured ? `Ask ${name}…` : "Set up in Settings first"}
          disabled={!configured || busy}
          className="flex-1 resize-none bg-gray-700 text-cyan-100 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-purple-500 outline-none text-sm max-h-28 placeholder-gray-500"
        />
        {settings && settings.stt && sttSupported() && (
          <button
            type="button"
            onClick={toggleListening}
            disabled={!configured || busy}
            className={`tap p-2 rounded-xl border-2 ${
              listening ? "bg-red-500/20 border-red-500/50 text-red-200 animate-pulse" : "bg-gray-700 border-gray-600 text-cyan-300"
            } disabled:opacity-40`}
            title={listening ? "Stop listening" : "Dictate"}
            aria-pressed={listening}
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
        <button
          type="submit"
          disabled={!configured || busy || !input.trim()}
          className="tap p-2 rounded-xl bg-purple-500/30 border-2 border-purple-400/50 text-purple-100 disabled:opacity-40"
          aria-label="Send"
          title="Send (Enter)"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </motion.div>
  );

  return (
    <>
      {/* Desktop sprite: draggable, wandering, click to chat */}
      {!mobile && (
        <motion.div
          className="fixed top-0 left-0 z-[47] select-none touch-none"
          style={{ x, y, width: SPRITE_W, height: SPRITE_H, cursor: dragging ? "grabbing" : "grab" }}
          drag
          dragMomentum={false}
          dragConstraints={dragConstraints}
          dragElastic={0.05}
          onDragStart={() => {
            setDragging(true);
            wanderRef.current.anims.forEach((a) => a && a.stop && a.stop());
          }}
          onDragEnd={onDragEnd}
          onTap={() => setOpen((v) => !v)}
          title={open ? `Hide ${name}` : `Talk to ${name}`}
          role="button"
          aria-label={open ? `Hide ${name} chat` : `Open ${name} chat`}
        >
          <AnimatePresence>
            {phase === "hatch" && (
              <motion.div
                key="ball"
                className="absolute inset-0 flex items-end justify-center pb-2"
                initial={{ opacity: 1, scale: 0.6 }}
                animate={{ opacity: [1, 1, 0], scale: [0.6, 1, 1.3] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.0, times: [0, 0.5, 1] }}
              >
                <WakeBall size={40} open />
              </motion.div>
            )}
          </AnimatePresence>
          <motion.div
            initial={{ scale: 0, y: 20 }}
            animate={
              phase === "hatch"
                ? { scale: [0, 0, 1.18, 0.94, 1], y: [20, 20, -8, 2, 0] }
                : { scale: 1, y: [0, -4, 0] }
            }
            transition={
              phase === "hatch"
                ? { duration: 1.1, times: [0, 0.45, 0.7, 0.85, 1], ease: "easeOut" }
                : { repeat: Infinity, duration: 2.6, ease: "easeInOut" }
            }
            style={{ scaleX: facing }}
            className="w-full h-full flex items-end justify-center"
          >
            <EggSprite size={SPRITE_W} mood={busy ? "busy" : mood} className="drop-shadow-lg" />
          </motion.div>
          {!open && messages.length === 0 && phase === "idle" && (
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-purple-200 bg-gray-800/90 border border-purple-500/40 rounded-full px-2 py-0.5 pointer-events-none">
              {name}
            </div>
          )}
        </motion.div>
      )}

      {/* Mobile: a floating bubble opens the sheet */}
      {mobile && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-3 bottom-[4.75rem] z-[47] glass rounded-full p-1.5 card-shadow"
          aria-label={`Open ${name} chat`}
          title={`Talk to ${name}`}
        >
          <EggSprite size={36} mood={busy ? "busy" : mood} />
        </button>
      )}

      <AnimatePresence>{open && panel}</AnimatePresence>
    </>
  );
};

export default AiCompanion;
